import { useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Editor } from 'primereact/editor';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useCreateClientIssueRequestMutation } from '@/app/services/issues';
import { addToast } from '@/app/slices/toast';
import { AccountAutoComplete } from '@/components/common';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { ToastLife } from '@/components/hoc/toast-container/enums';

import { clientIssueSchema, ClientIssueValues } from './schema';

import './ClientIssue.css';

type ClientIssueProps = {
  className?: string;
};

export const ClientIssue = ({ className }: ClientIssueProps) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ClientIssueValues>({ resolver: zodResolver(clientIssueSchema) });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [createClientIssueRequest] = useCreateClientIssueRequestMutation();

  const onSubmit = useCallback(
    async (values: ClientIssueValues) => {
      const newValues = {
        ...values,
        implantedOn: new Date(values.implantedOn).toISOString(),
      };
      try {
        const res = await createClientIssueRequest({ input: newValues });
        dispatch(
          addToast({
            severity: 'success',
            summary: t('issues.issueSubmitted'),
            detail: t('issues.issueSubmittedDetail'),
            life: ToastLife.DEFAULT,
          }),
        );

        if (res.data) {
          navigate(`/issues/${res.data.createClientIssueRequest.id}`);
        }
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('issues.submissionFailed'),
            detail: t('issues.submissionFailedDetail') + error,
            life: ToastLife.DEFAULT,
          }),
        );
      }
    },
    [createClientIssueRequest, dispatch, navigate],
  );

  return (
    <div className={`client-issue ${className || ''}`}>
      <div className="issue-type-picker" />
      <form onSubmit={handleSubmit(onSubmit)} className="client-issue-form">
        <div className="client-issue-form__fields">
          <div className="label-input-group">
            <label htmlFor="title">{t('common.title')}</label>
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  className={classNames({ 'p-invalid': fieldState.error })}
                />
              )}
            />
            {errors.title && (
              <div className="errors">{errors.title.message}</div>
            )}
          </div>
          <div className="label-input-group">
            <label htmlFor="implantedOn">{t('transfer.implantDate')}</label>
            <Controller
              name="implantedOn"
              control={control}
              render={({ field, fieldState }) => (
                <Calendar
                  id={field.name}
                  {...field}
                  className={classNames({ 'p-invalid': fieldState.error })}
                  dateFormat="mm/dd/yy"
                  showIcon
                  value={field.value ? new Date(field.value) : null}
                />
              )}
            />
            {errors.implantedOn && (
              <div className="errors">{errors.implantedOn.message}</div>
            )}
          </div>

          <div className="label-input-group">
            <label htmlFor="embryosPerFertilization">
              {t('embryo.embryosPerFertilization')}
            </label>
            <Controller
              name="embryosPerFertilization"
              control={control}
              render={({ field, fieldState }) => (
                <InputNumber
                  id={field.name}
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  className={classNames({ 'p-invalid': fieldState.error })}
                />
              )}
            />
            {errors.embryosPerFertilization && (
              <div className="errors">
                {errors.embryosPerFertilization.message}
              </div>
            )}
          </div>

          <div className="label-input-group">
            <Controller
              name="accountId"
              control={control}
              render={({ field, fieldState }) => (
                <AccountAutoComplete
                  label="Account"
                  field="name"
                  value={field.value}
                  onSelect={(e: AutoCompleteSelectEvent) =>
                    field.onChange(e.value.id)
                  }
                  className={classNames({ 'p-invalid': fieldState.error })}
                />
              )}
            />
            {errors.accountId && (
              <div className="errors">{errors.accountId.message}</div>
            )}
          </div>

          <div className="label-input-group">
            <label htmlFor="content">{t('common.description')}</label>
            <Controller
              name="content"
              control={control}
              render={({ field, fieldState }) => (
                <Editor
                  id={field.name}
                  {...field}
                  className={classNames('issues-editor', {
                    'p-invalid': fieldState.error,
                  })}
                  onTextChange={(e) => field.onChange(e.htmlValue)}
                />
              )}
            />
            {errors.content && (
              <div className="errors">{errors.content.message}</div>
            )}
          </div>
        </div>
        <div className="client-issue-form__submit">
          <Button
            icon="pi pi-check"
            label={t('common.submit')}
            type="submit"
            className="submit-button"
          />
        </div>
      </form>
    </div>
  );
};
